import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { handleReauth } from "@/lib/reauth";
import { Play, ExternalLink, Clock, Mailbox, Inbox, AlertCircle, CalendarClock, ArrowUpRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function StatusCard({ label, value, sub, icon, testid, accent }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E8E6DF] rounded-lg p-6 transition-all hover:shadow-sm">
      <div className="flex items-start justify-between">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891]">{label}</p>
        <span className={`h-8 w-8 rounded-md flex items-center justify-center ${accent || "bg-[#F3F1EC] text-[#971a8c]"}`}>{icon}</span>
      </div>
      <div className="mt-4 font-heading text-3xl text-[#1C2321] font-medium">{value}</div>
      {sub && <div className="mt-1 text-sm text-[#5D6D63]">{sub}</div>}
    </div>
  );
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [followUps, setFollowUps] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dashboard/follow-ups?days=7"),
      ]);
      setSummary(s.data);
      setFollowUps(f.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (days = 1) => {
    setRunning(true);
    const label = days > 1 ? `Catching up on ${days} days` : "Scan started";
    toast.message(label, { description: "This can take up to a minute for a busy inbox." });
    try {
      const r = await api.post("/scan/run-now", { days });
      if (r.data.status === "success") {
        const dupNote = (r.data.skipped_duplicate || 0) > 0
          ? ` · ${r.data.skipped_duplicate} skipped as duplicates`
          : "";
        toast.success(`Scanned ${r.data.scanned} emails · Logged ${r.data.logged}${dupNote}`);
      } else {
        toast.error(r.data.error_message || "Scan finished with errors");
      }
      await load();
      await refresh();
    } catch (e) {
      handleReauth(e, "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const openSheet = () => {
    if (summary?.sheet_url) window.open(summary.sheet_url, "_blank", "noopener");
    else toast.message("Your Google Sheet will be created on the first scan.");
  };

  const lastRun = summary?.last_run;

  return (
    <div data-testid={DASHBOARD.root}>
      <div className="flex items-start justify-between mb-8 gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-2">Dashboard</p>
          <h1 className="font-heading text-4xl tracking-tight text-[#1C2321] font-medium">
            Good{greet()}, {user?.name?.split(" ")[0] || "teacher"}.
          </h1>
          <p className="mt-2 text-base text-[#5D6D63] max-w-xl">
            Here&apos;s a snapshot of your parent communications. Your daily scan runs quietly in the background.
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid={DASHBOARD.openSheetBtn}
            variant="outline"
            onClick={openSheet}
            className="h-11 rounded-md border-[#E8E6DF] hover:bg-[#F3F1EC]"
          >
            <ExternalLink className="h-4 w-4 mr-2" /> Open Google Sheet
          </Button>
          <div className="inline-flex items-stretch rounded-md overflow-hidden">
            <Button
              data-testid={DASHBOARD.runNowBtn}
              onClick={() => runNow(1)}
              disabled={running}
              className="h-11 rounded-none rounded-l-md px-5 bg-[#ec197f] text-white hover:bg-[#d1156e] transition-colors border-r border-white/20"
            >
              <Play className="h-4 w-4 mr-2" /> {running ? "Scanning…" : "Run scan now"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="dashboard-scan-catchup-btn"
                  disabled={running}
                  aria-label="Catch-up scan options"
                  className="h-11 rounded-none rounded-r-md px-2 bg-[#ec197f] text-white hover:bg-[#d1156e] transition-colors"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Catch-up scan</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="scan-window-1"
                  onSelect={() => runNow(1)}
                  disabled={running}
                >
                  Last 1 day <span className="ml-auto text-xs text-[#8B9891]">default</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="scan-window-3"
                  onSelect={() => runNow(3)}
                  disabled={running}
                >
                  Last 3 days
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="scan-window-7"
                  onSelect={() => runNow(7)}
                  disabled={running}
                >
                  Last 7 days
                </DropdownMenuItem>
                <div className="px-2 py-1.5 text-[11px] text-[#8B9891]">
                  Duplicates are skipped automatically.
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 mb-10">
        <StatusCard
          testid={DASHBOARD.totalCard}
          label="Total logged"
          value={summary?.total_entries ?? "—"}
          sub={
            (summary?.total_outgoing ?? 0) > 0
              ? `Since you connected · ${summary.total_outgoing} outgoing to parents`
              : "Since you connected"
          }
          icon={<Inbox className="h-4 w-4" />}
        />
        <StatusCard
          testid={DASHBOARD.needsReplyCard}
          label="Needs reply"
          value={summary?.needs_reply ?? "—"}
          sub="Awaiting your response"
          icon={<Mailbox className="h-4 w-4" />}
          accent="bg-[#F9EAEA] text-[#E07A5F]"
        />
        <StatusCard
          testid={DASHBOARD.nextRunCard}
          label="Next scan"
          value={fmtDT(summary?.next_run)}
          sub={user?.paused ? "Currently paused" : "Runs automatically"}
          icon={<Clock className="h-4 w-4" />}
        />
        <StatusCard
          testid={DASHBOARD.lastRunCard}
          label="Last scan"
          value={fmtDT(lastRun?.started_at)}
          sub={
            lastRun
              ? `Scanned ${lastRun.scanned} · Logged ${lastRun.logged} · Skipped ${lastRun.skipped_non_parent + lastRun.skipped_duplicate}`
              : "No scans yet"
          }
          icon={<AlertCircle className="h-4 w-4" />}
          accent={lastRun?.status === "error" ? "bg-[#F9EAEA] text-[#E07A5F]" : "bg-[#e7f5fb] text-[#971a8c]"}
        />
      </div>

      <FollowUpsWidget data={followUps} />

      <div className="bg-white border border-[#E8E6DF] rounded-lg p-8">
        <h2 className="font-heading text-2xl text-[#1C2321] font-medium mb-2">How ContactAssist works</h2>
        <p className="text-[#5D6D63] max-w-2xl mb-6">
          Once a day at your chosen time, we fetch the last 24 hours of inbox mail, ask Claude Sonnet 4.5 to identify parent-about-student messages, and append rows to your Google Sheet. Rows you edit (Notes, Response Status) are never overwritten. Duplicate emails are skipped automatically. We also scan your <strong>Sent</strong> folder to log outgoing messages to parents on a separate tab in the same Sheet — so your Contact Log covers both sides of every conversation.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Step n="1" title="Connect" body="Sign in with your school Google account. Grant Gmail access (read messages + save AI-drafted replies as drafts — nothing is sent without you clicking Send) and access to one Google Sheet in your Drive." />
          <Step n="2" title="Schedule" body="Pick a daily time outside school hours. Your timezone is detected automatically." />
          <Step n="3" title="Review" body="Skim the log here or in Google Sheets. Mark replies, add notes, done." />
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="p-5 rounded-md bg-[#F9F8F6] border border-[#E8E6DF]">
      <div className="h-8 w-8 rounded-full bg-[#971a8c] text-white flex items-center justify-center font-medium">{n}</div>
      <div className="mt-3 font-heading text-lg text-[#1C2321]">{title}</div>
      <div className="mt-1 text-sm text-[#5D6D63]">{body}</div>
    </div>
  );
}

function FollowUpsWidget({ data }) {
  const items = data?.items || [];
  const days = data?.horizon_days || 7;

  // Only render section wrapper if we have data OR data loaded with zero items (skip loading flicker)
  if (data === null) return null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch { return iso; }
  };

  return (
    <section
      data-testid="dashboard-followups"
      className="mb-10 bg-white border border-[#E8E6DF] rounded-lg overflow-hidden"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E6DF] bg-[#F9F8F6]">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-[#ec197f]/10 text-[#ec197f] flex items-center justify-center">
            <CalendarClock className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-heading text-lg text-[#1C2321] font-medium leading-none">
              Follow-ups due this week
            </h2>
            <p className="text-xs text-[#8B9891] mt-1">
              Deadlines you promised parents in the next {days} days (plus recently overdue).
            </p>
          </div>
        </div>
        <span
          data-testid="dashboard-followups-count"
          className="text-xs font-semibold text-[#8B9891] tabular-nums"
        >
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-[#8B9891]" data-testid="dashboard-followups-empty">
          Nothing due yet. When your outgoing scan finds a deadline you mentioned to a parent, it&apos;ll show up here.
        </div>
      ) : (
        <ul className="divide-y divide-[#E8E6DF]">
          {items.map((it) => (
            <li
              key={it.entry_id}
              data-testid="dashboard-followup-item"
              className="px-6 py-4 flex items-start justify-between gap-4 hover:bg-[#F9F8F6] transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#1C2321]">
                    {it.student_name || it.parent_name || it.parent_email || "Unknown"}
                  </span>
                  {it.parent_name && it.student_name && (
                    <span className="text-xs text-[#8B9891]">· {it.parent_name}</span>
                  )}
                  {it.overdue && (
                    <span
                      data-testid="dashboard-followup-overdue-pill"
                      className="text-[10px] uppercase tracking-wider font-semibold bg-[#F9EAEA] text-[#E07A5F] px-2 py-0.5 rounded"
                    >
                      Overdue
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#5D6D63] mt-1 truncate">
                  {it.follow_up_needed || it.summary || it.subject}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-medium text-[#1C2321] tabular-nums">
                  {fmtDate(it.deadline_at)}
                </div>
                {it.permalink && (
                  <a
                    href={it.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-[#971a8c] hover:underline"
                    data-testid="dashboard-followup-open-email"
                  >
                    Open <ArrowUpRight className="h-3 w-3" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function greet() {
  const h = new Date().getHours();
  if (h < 12) return " morning";
  if (h < 18) return " afternoon";
  return " evening";
}
